<script setup lang="ts">
import { ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type {
  PushApp,
  WechatChannelCode,
  WechatChannelTemplate,
  WechatSubscription,
  WechatUserBinding
} from './types'
import { pushAppOptions } from './types'
import { formatTime, statusText } from './usePushCenterState'
import { Plus, Users, BellRing, FileCode } from '@lucide/vue'
import {
  AdminDataTable,
  AdminDialog,
  AdminFilterBar,
  AdminStatusTag,
  RrButton,
  RrInput,
  RrSelect,
  RrSwitch,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    templates?: WechatChannelTemplate[]
    bindings?: WechatUserBinding[]
    subscriptions?: WechatSubscription[]
    apps?: PushApp[]
    appCode?: string
    templateTotal?: number
    templatePage?: number
    templatePageSize?: number
    bindingTotal?: number
    bindingPage?: number
    bindingPageSize?: number
    subscriptionTotal?: number
    subscriptionPage?: number
    subscriptionPageSize?: number
    queryLoading?: boolean
    keyword?: string
    canManage?: boolean
    createApiClient: () => ApiClient
  }>(),
  {
    templates: () => [],
    bindings: () => [],
    subscriptions: () => [],
    apps: () => [],
    appCode: '',
    canManage: false,
    templateTotal: 0,
    templatePage: 1,
    templatePageSize: 10,
    bindingTotal: 0,
    bindingPage: 1,
    bindingPageSize: 10,
    subscriptionTotal: 0,
    subscriptionPage: 1,
    subscriptionPageSize: 10,
    queryLoading: false,
    keyword: ''
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  search: [keyword: string]
  'page-change': [tab: WechatSubTab, page: number, pageSize: number]
  refresh: []
  notice: [msg: string]
  error: [msg: string]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  localAppCode.value = code
  emit('update:appCode', code)
}

type WechatSubTab = 'templates' | 'bindings' | 'subscriptions'
const currentSubTab = ref<WechatSubTab>('templates')

const keyword = ref(props.keyword)
watch(
  () => props.keyword,
  (value) => {
    keyword.value = value
  }
)
const dialogVisible = ref(false)
const loading = ref(false)

// 1. 模板映射表格配置
const templateColumns: TableColumnType<WechatChannelTemplate>[] = [
  { key: 'appCode', title: '所属应用', width: 110 },
  { key: 'eventCode', title: '业务事件编码', width: 180 },
  { key: 'channelCode', title: '微信渠道', width: 140 },
  { key: 'providerTemplateId', title: '微信模板 ID', minWidth: 200 },
  { key: 'pagePath', title: '小程序路径', minWidth: 160 },
  { key: 'status', title: '状态', width: 90 },
  { key: 'actions', title: '操作', width: 90, align: 'right', fixed: 'right' }
]

// 2. 微信用户绑定表格配置
const bindingColumns: TableColumnType<WechatUserBinding>[] = [
  { key: 'id', title: '绑定 ID', width: 90 },
  { key: 'clientUserId', title: '用户 ID', width: 110 },
  { key: 'appCode', title: '所属应用', width: 110 },
  { key: 'channelType', title: '渠道类型', width: 130 },
  { key: 'appId', title: '微信 AppID', width: 160 },
  { key: 'openIdMasked', title: 'OpenID (脱敏)', minWidth: 180 },
  { key: 'followed', title: '关注公众号', width: 100 },
  { key: 'enabled', title: '状态', width: 90 },
  { key: 'lastSeenAt', title: '最近活跃', width: 170 }
]

// 3. 订阅授权明细表格配置
const subscriptionColumns: TableColumnType<WechatSubscription>[] = [
  { key: 'id', title: '授权 ID', width: 90 },
  { key: 'bindingId', title: '绑定 ID', width: 100 },
  { key: 'clientUserId', title: '用户 ID', width: 110 },
  { key: 'providerTemplateId', title: '微信模板 ID', minWidth: 180 },
  { key: 'authorizationType', title: '授权类型', width: 110 },
  { key: 'status', title: '授权结果', width: 100 },
  { key: 'remainingCount', title: '剩余可用次数', width: 120 },
  { key: 'authorizedAt', title: '授权时间', width: 170 },
  { key: 'consumedAt', title: '核销时间', width: 170 }
]

// 表单对象（动态绑定当前应用，杜绝写死 guoxue）
const form = ref({
  id: null as number | null,
  appCode: localAppCode.value || props.appCode,
  eventCode: '',
  channelCode: 'WECHAT_MINIAPP_SUBSCRIBE' as WechatChannelCode,
  providerTemplateId: '',
  dataMapping: '{\n  "thing1": "{{title}}",\n  "time2": "{{createTime}}"\n}',
  pagePath: '',
  miniappState: 'formal',
  language: 'zh_CN',
  enabled: true
})

function handleSearch() {
  emit('search', keyword.value.trim())
}

function handleReset() {
  keyword.value = ''
  emit('search', '')
}

function openDialog(item?: WechatChannelTemplate) {
  if (item) {
    let mapping = ''
    if (typeof item.dataMappingJson === 'string') {
      try {
        mapping = JSON.stringify(JSON.parse(item.dataMappingJson), null, 2)
      } catch {
        mapping = item.dataMappingJson
      }
    } else if (item.dataMappingJson) {
      mapping = JSON.stringify(item.dataMappingJson, null, 2)
    } else if (item.dataMapping) {
      mapping = JSON.stringify(item.dataMapping, null, 2)
    }

    form.value = {
      id: item.id,
      appCode: item.appCode,
      eventCode: item.eventCode,
      channelCode: item.channelCode,
      providerTemplateId: item.providerTemplateId,
      dataMapping: mapping || '{\n  "thing1": "{{title}}"\n}',
      pagePath: item.pagePath || '',
      miniappState: item.miniappState || 'formal',
      language: item.language || 'zh_CN',
      enabled: item.enabled
    }
  } else {
    form.value = {
      id: null,
      appCode: localAppCode.value || props.appCode,
      eventCode: '',
      channelCode: 'WECHAT_MINIAPP_SUBSCRIBE',
      providerTemplateId: '',
      dataMapping: '{\n  "thing1": "{{title}}",\n  "time2": "{{createTime}}"\n}',
      pagePath: '',
      miniappState: 'formal',
      language: 'zh_CN',
      enabled: true
    }
  }
  dialogVisible.value = true
}

async function save() {
  if (!props.canManage) return
  if (!form.value.appCode || !form.value.eventCode || !form.value.providerTemplateId) {
    emit('error', '请填写完整应用编码、业务事件与微信模板 ID')
    return
  }
  loading.value = true
  try {
    const api = props.createApiClient()
    const payload = {
      appCode: form.value.appCode,
      eventCode: form.value.eventCode,
      channelCode: form.value.channelCode,
      providerTemplateId: form.value.providerTemplateId,
      dataMappingJson: form.value.dataMapping,
      pagePath: form.value.pagePath,
      miniappState: form.value.miniappState,
      language: form.value.language,
      enabled: form.value.enabled
    }
    if (form.value.id) {
      await api.put(`/api/saas/push/wechat/channel-templates/${form.value.id}`, payload)
    } else {
      await api.post('/api/saas/push/wechat/channel-templates', payload)
    }
    emit('notice', `微信模板映射 ${form.value.eventCode} 已保存`)
    dialogVisible.value = false
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '微信模板保存失败')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="push-tab-pane">
    <!-- 顶部子 Tab 切换条 -->
    <div class="sub-tab-header">
      <div class="sub-tabs-pill">
        <button
          type="button"
          class="sub-tab-btn"
          :class="{ active: currentSubTab === 'templates' }"
          @click="currentSubTab = 'templates'"
        >
          <FileCode :size="15" />
          模板映射配置
          <span class="pill-count">{{ templateTotal }}</span>
        </button>
        <button
          type="button"
          class="sub-tab-btn"
          :class="{ active: currentSubTab === 'bindings' }"
          @click="currentSubTab = 'bindings'"
        >
          <Users :size="15" />
          微信用户绑定
          <span class="pill-count">{{ bindingTotal }}</span>
        </button>
        <button
          type="button"
          class="sub-tab-btn"
          :class="{ active: currentSubTab === 'subscriptions' }"
          @click="currentSubTab = 'subscriptions'"
        >
          <BellRing :size="15" />
          订阅授权监控
          <span class="pill-count">{{ subscriptionTotal }}</span>
        </button>
      </div>
    </div>

    <!-- 筛选控制栏 -->
    <AdminFilterBar :loading="queryLoading" @submit="handleSearch" @reset="handleReset">
      <label>
        所属应用
        <RrSelect v-model:value="localAppCode" :options="pushAppOptions(apps)" @change="onAppChange" />
      </label>
      <label>
        {{ currentSubTab === 'bindings' ? '用户 ID / AppID' : '关键词搜索' }}
        <RrInput
          v-model:value="keyword"
          :placeholder="currentSubTab === 'templates' ? '事件编码 / 模板ID' : currentSubTab === 'bindings' ? '用户ID / AppID' : '模板ID / 用户ID'"
          @keyup.enter="handleSearch"
        />
      </label>
      <template #actions>
        <RrButton
          v-if="currentSubTab === 'templates'"
          type="primary"
          :disabled="!canManage"
          @click="openDialog()"
        >
          <template #icon><Plus :size="16" /></template>
          新建微信映射
        </RrButton>
      </template>
    </AdminFilterBar>

    <!-- 1. 模板映射视图 -->
    <AdminDataTable
      v-if="currentSubTab === 'templates'"
      :columns="templateColumns"
      :data-source="templates"
      :loading="queryLoading"
      :current="templatePage"
      :page-size="templatePageSize"
      :total="templateTotal"
      :empty="templates.length === 0"
      empty-title="暂无微信事件模板映射"
      empty-description="可点击右上角「新建微信映射」配置小程序订阅或服务号模板。"
      @update:current="(p) => emit('page-change', 'templates', p, templatePageSize)"
      @update:page-size="(s) => emit('page-change', 'templates', 1, s)"
      @refresh="emit('refresh')"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'channelCode'">
          <RrTag
            :tone="record.channelCode === 'WECHAT_MINIAPP_SUBSCRIBE' ? 'primary' : 'success'"
            :label="record.channelCode === 'WECHAT_MINIAPP_SUBSCRIBE' ? '小程序订阅' : '公众号模板'"
          />
        </template>
        <template v-else-if="column.key === 'pagePath'">
          {{ record.pagePath || '-' }}
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag
            :text="statusText(record.enabled)"
            :tone="record.enabled ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrButton type="text" size="small" :disabled="!canManage" @click="openDialog(record)">
            编辑
          </RrButton>
        </template>
      </template>
    </AdminDataTable>

    <!-- 2. 用户微信绑定视图 -->
    <AdminDataTable
      v-else-if="currentSubTab === 'bindings'"
      :columns="bindingColumns"
      :data-source="bindings"
      :loading="queryLoading"
      :current="bindingPage"
      :page-size="bindingPageSize"
      :total="bindingTotal"
      :empty="bindings.length === 0"
      empty-title="暂无微信用户绑定记录"
      empty-description="当客户端用户通过微信小程序登录或授权关注公众号时，绑定关系将自动记录。"
      @update:current="(p) => emit('page-change', 'bindings', p, bindingPageSize)"
      @update:page-size="(s) => emit('page-change', 'bindings', 1, s)"
      @refresh="emit('refresh')"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'channelType'">
          <RrTag
            :tone="record.channelType === 'WECHAT_MINIAPP_SUBSCRIBE' ? 'primary' : 'success'"
            :label="record.channelType === 'WECHAT_MINIAPP_SUBSCRIBE' ? '小程序' : '服务号'"
          />
        </template>
        <template v-else-if="column.key === 'openIdMasked'">
          <code>{{ record.openIdMasked || '-' }}</code>
        </template>
        <template v-else-if="column.key === 'followed'">
          <AdminStatusTag
            :text="record.followed ? '已关注' : '未关注'"
            :tone="record.followed ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'enabled'">
          <AdminStatusTag
            :text="statusText(record.enabled)"
            :tone="record.enabled ? 'success' : 'danger'"
          />
        </template>
        <template v-else-if="column.key === 'lastSeenAt'">
          {{ formatTime(record.lastSeenAt) }}
        </template>
      </template>
    </AdminDataTable>

    <!-- 3. 订阅授权明细视图 -->
    <AdminDataTable
      v-else-if="currentSubTab === 'subscriptions'"
      :columns="subscriptionColumns"
      :data-source="subscriptions"
      :loading="queryLoading"
      :current="subscriptionPage"
      :page-size="subscriptionPageSize"
      :total="subscriptionTotal"
      :empty="subscriptions.length === 0"
      empty-title="暂无微信订阅授权明细"
      empty-description="用户在小程序端触发订阅弹窗并允许授权后，可在此查看授权余量与核销状态。"
      @update:current="(p) => emit('page-change', 'subscriptions', p, subscriptionPageSize)"
      @update:page-size="(s) => emit('page-change', 'subscriptions', 1, s)"
      @refresh="emit('refresh')"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'authorizationType'">
          <RrTag
            :tone="record.authorizationType === 'LONG_TERM' ? 'success' : 'primary'"
            :label="record.authorizationType === 'LONG_TERM' ? '长期授权' : '单次授权'"
          />
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag
            :text="record.status === 'ACCEPTED' ? '已同意' : record.status === 'REJECTED' ? '已拒绝' : record.status"
            :tone="record.status === 'ACCEPTED' ? 'success' : 'danger'"
          />
        </template>
        <template v-else-if="column.key === 'remainingCount'">
          <strong>{{ record.remainingCount ?? 0 }} 次</strong>
        </template>
        <template v-else-if="column.key === 'authorizedAt'">
          {{ formatTime(record.authorizedAt) }}
        </template>
        <template v-else-if="column.key === 'consumedAt'">
          {{ formatTime(record.consumedAt) }}
        </template>
      </template>
    </AdminDataTable>

    <!-- 模板映射编辑弹窗 -->
    <AdminDialog
      v-model:open="dialogVisible"
      :title="form.id ? '编辑微信模板映射' : '新建微信模板映射'"
      width="580px"
      @cancel="dialogVisible = false"
    >
      <div class="wechat-dialog-content">
        <div class="form-row">
          <label class="field-label">所属应用 <span class="req">*</span></label>
          <RrSelect
            v-model:value="form.appCode"
            :options="pushAppOptions(apps).filter((o) => o.value !== '')"
          />
        </div>

        <div class="form-row">
          <label class="field-label">触达渠道 <span class="req">*</span></label>
          <RrSelect
            v-model:value="form.channelCode"
            :options="[
              { label: '微信小程序订阅消息 (WECHAT_MINIAPP_SUBSCRIBE)', value: 'WECHAT_MINIAPP_SUBSCRIBE' },
              { label: '微信公众号模板消息 (WECHAT_MP_TEMPLATE)', value: 'WECHAT_MP_TEMPLATE' }
            ]"
          />
        </div>

        <div class="form-row">
          <label class="field-label">业务事件编码 <span class="req">*</span></label>
          <RrInput v-model:value="form.eventCode" placeholder="如: ORDER_PAID, SYSTEM_ALERT, COMMENT_REPLY" />
        </div>

        <div class="form-row">
          <label class="field-label">微信模板 ID <span class="req">*</span></label>
          <RrInput v-model:value="form.providerTemplateId" placeholder="微信后台分配的 template_id" />
        </div>

        <div v-if="form.channelCode === 'WECHAT_MINIAPP_SUBSCRIBE'" class="form-row">
          <label class="field-label">小程序跳转页面</label>
          <RrInput v-model:value="form.pagePath" placeholder="如: pages/index/index?orderId={{orderId}}" />
        </div>

        <div class="form-row">
          <label class="field-label">字段映射 JSON <span class="req">*</span></label>
          <RrInput
            v-model:value="form.dataMapping"
            type="textarea"
            :rows="5"
            class="mono-font"
            placeholder="JSON 格式字段映射"
          />
        </div>

        <div class="form-row">
          <label class="field-label">启用状态</label>
          <RrSwitch v-model:checked="form.enabled" />
        </div>
      </div>

      <template #footer>
        <RrButton @click="dialogVisible = false">取消</RrButton>
        <RrButton type="primary" :disabled="loading" @click="save">
          {{ loading ? '保存中...' : '确定保存' }}
        </RrButton>
      </template>
    </AdminDialog>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}

.sub-tab-header {
  display: flex;
  align-items: center;
}

.sub-tabs-pill {
  display: flex;
  gap: var(--rr-space-1);
  background: var(--rr-color-canvas);
  padding: var(--rr-space-1);
  border-radius: var(--rr-radius-control);
}

.sub-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-2);
  padding: var(--rr-space-2) var(--rr-space-3);
  border: none;
  background: transparent;
  border-radius: var(--rr-radius-control);
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
  cursor: pointer;
  transition:
    color var(--rr-motion-duration-mid) var(--rr-motion-ease-out),
    background var(--rr-motion-duration-mid) var(--rr-motion-ease-out);
}

.sub-tab-btn:hover {
  color: var(--rr-color-text);
}

.sub-tab-btn.active {
  background: var(--rr-color-surface);
  color: var(--rr-color-primary);
  font-weight: var(--rr-font-weight-bold);
  box-shadow: var(--rr-shadow-1);
}

.pill-count {
  font-size: var(--rr-font-size-xs);
  padding: 0 var(--rr-space-2);
  border-radius: var(--rr-radius-full);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}

.sub-tab-btn.active .pill-count {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}

.wechat-dialog-content {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}

.field-label {
  font-size: var(--rr-font-size-sm);
  font-weight: var(--rr-font-weight-medium);
  color: var(--rr-color-text);
}

.field-label .req {
  color: var(--rr-color-danger);
}

.mono-font :deep(.rr-textarea) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
</style>
